import { FC, useEffect } from 'react';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { useApiCall, useModal } from '@/hooks';
import { profileActions } from '@/redux/actions';
import { AmlHistoryItemData } from '@/redux/models';
import { profileSelectors } from '@/redux/selectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { MonitoringSubscribeRequest, amlbotAPI } from '@/api';
import { toast } from 'react-toastify';
import { COMMON_CONTAINER_ID, ChainsDictionary } from '@/config';

import { ConfirmActionModal } from '@/components/_modals';
import { AlertBox } from '@/components/AlertBox';
import styles from './MonitoringSubscribe.module.sass';

interface MonitoringSubscribeProps {
  children: (openModal: any) => void;
  item: AmlHistoryItemData;
}

const MonitoringSubscribe: FC<MonitoringSubscribeProps> = ({
  children,
  item,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    isOpen: isOpenConfirmModal,
    onOpenModal: handleOpenConfirmModal,
    onCloseModal: handleCloseConfirmModal,
  } = useModal();
  const monitoringRiskThreshold = useAppSelector(
    profileSelectors.monitoringRiskThreshold,
  );
  const networkName = ChainsDictionary[item.network]?.name || item.network;

  const [subscribe, subscribeLoading, subscribeError, subscribeErrorCode] =
    useApiCall(async () => {
      handleCloseConfirmModal();

      const payload: MonitoringSubscribeRequest = {
        uuid: item.uuid,
      };

      const result = await amlbotAPI.monitoringSubscribe(payload);

      if (result.success) {
        const monitoring = result.data.response;

        toast.success(
          t('translation.monitoring_success_add', {
            address: monitoring?.address,
            blockchain: networkName,
          }),
          {
            containerId: COMMON_CONTAINER_ID,
          },
        );

        dispatch(profileActions.getProfileInfo());
      }
    });

  useEffect(() => {
    if (!subscribeLoading && !!subscribeError) {
      toast.error(t('translation.subscribe_error', { error: subscribeError }), {
        containerId: COMMON_CONTAINER_ID,
      });
      handleCloseConfirmModal();
    }
  }, [subscribeLoading]);

  const triggerSubscribe = () => {
    subscribe();
  };

  const openModal = () => {
    if (
      Number(item.riskscore) >= monitoringRiskThreshold ||
      item.hasBlackListFlag
    ) {
      toast.error(
        t('translation.subscribe_risky_error', {
          score: monitoringRiskThreshold,
        }),
        {
          containerId: COMMON_CONTAINER_ID,
        },
      );
      return;
    }

    handleOpenConfirmModal();
  };

  return (
    <>
      {children(openModal)}
      <ConfirmActionModal
        title={t('translation.alert_warning_title')}
        isOpen={isOpenConfirmModal}
        onClose={handleCloseConfirmModal}
        onConfirm={triggerSubscribe}
        confirmButtonText={t('translation.subscribe_btn')}
        cancelButtonText={t('translation.cancel_btn')}
      >
        <span>
          {parse(
            t('translation.monitoring_prompt', {
              address: item.address,
              blockchain: networkName,
            }),
          )}
          <AlertBox type="info" small>
            {{
              description: parse(t('translation.subscribe_description_modal')),
            }}
          </AlertBox>
        </span>
      </ConfirmActionModal>
    </>
  );
};

export default MonitoringSubscribe;
